import { Menu, MenuDivider, MenuItem } from "@blueprintjs/core";
import { ReactFlowProvider, useReactFlow, type Viewport } from "@xyflow/react";
import "@xyflow/react/dist/base.css";
import classNames from "classnames";
import { observer } from "mobx-react";
import { AnimatePresence, motion } from "motion/react";
import { Search } from "modules/common/components/form/Search";
import { showContextMenu } from "modules/common/services/context-menu";
import { useMst } from "modules/root/models/hooks";
import { can, canAny } from "modules/session/auth/access";
import spendingFunc from "modules/spending/functionalities";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ancestors,
    buildTree,
    countLines,
    employeeRole,
    layoutTree,
    NORMAL_SIZES,
    searchTree,
    sortUnits,
    TreeItem,
    vacancyRole,
} from "../layout";
import { OrgTreeStoreType, UnitPatch } from "../org-tree-store";
import { OrgEmployeeData, OrgUnitData, ROOT_ID } from "../types";
import { TreeActions, TreeActionsContext, TreeState, TreeStateContext } from "./context";
import { AddUnitForm, AnchoredPopover, DiscardChangesDialog, MoveEmployeeDialog, TypesDialog } from "./dialogs";
import { EmployeeCard } from "./EmployeeCard";
import { SelectField } from "./fields";
import { HistoryPanel } from "./HistoryPanel";
import { PositionsDictionaryDialog } from "../../components/PositionsDictionaryDialog";
import { DEPARTMENT_ROLES, OneCImportDialog, STAFF_ROLES } from "modules/spending/onec/components/OneCImportDialog";
import {
    BriefcaseIcon,
    CollapseIcon,
    ExpandAllIcon,
    ExpandIcon,
    FitIcon,
    HistoryIcon,
    ImportIcon,
    MinusIcon,
    PlusIcon,
    TagIcon,
    UndoIcon,
} from "./icons";
import { DropRequest, DropTarget, OrgCanvas } from "./OrgCanvas";
import styles from "./OrgTree.module.scss";
import { UnitPanel } from "./UnitPanel";

/**
 * Дерево структуры организации (ТЗ «Структура организации», 07.09.2026).
 *
 * Просмотр — всем сотрудникам; правка — с правом на структуру. Правки сохраняются
 * сразу (индикатор справа), последнюю можно отменить (Ctrl+Z). Должность в дереве не
 * узел, а подпись в карточке сотрудника.
 */

const EXPANDED_KEY = "org-tree.expanded";

// вторая строка обычной карточки сотрудника (должность · ранг) переносится целиком, без многоточия:
// ширина колонки текста = карточка − рамка, поля, аватар и зазор (.employee в OrgTree.module.scss)
const ROLE_FONT = '11px "GothamPro", Arial, sans-serif';
// у вакансии должность жирнее обычной (.employee.vacancy .role) — шире, меряем тем же начертанием
const VACANCY_ROLE_FONT = `500 ${ROLE_FONT}`;
const ROLE_LINE = 14;
const ROLE_WIDTH = NORMAL_SIZES.employee.w - 3 - 9 - 10 - 30 - 10 - 2;

/** Высота карточки сотрудника по числу строк должности; пересчитывается, когда догрузится шрифт. */
function useEmployeeHeight() {
    const [fontsReady, setFontsReady] = useState(false);
    useEffect(() => {
        let alive = true;
        document.fonts?.ready.then(() => alive && setFontsReady(true));
        return () => {
            alive = false;
        };
    }, []);

    return useMemo(() => {
        const ctx = document.createElement("canvas").getContext("2d");
        if (!ctx) {
            return undefined;
        }
        const cache = new Map<string, number>();
        const measure = (s: string) => ctx.measureText(s).width;
        return (item: TreeItem) => {
            const vacancy = !item.employee;
            const role = vacancy ? vacancyRole(item.vacancy!) : employeeRole(item.employee!);
            const key = `${vacancy ? "v" : "e"}:${role}`;
            let lines = cache.get(key);
            if (lines === undefined) {
                ctx.font = vacancy ? VACANCY_ROLE_FONT : ROLE_FONT;
                lines = countLines(role, ROLE_WIDTH, measure);
                cache.set(key, lines);
            }
            return NORMAL_SIZES.employee.h + (lines - 1) * ROLE_LINE;
        };
        // fontsReady — чтобы перемерить настоящим шрифтом, а не запасным
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fontsReady]);
}

const readJson = <T,>(key: string, fallback: T): T => {
    try {
        const raw = window.localStorage.getItem(key);
        return raw ? (JSON.parse(raw) as T) : fallback;
    } catch {
        return fallback;
    }
};

const writeJson = (key: string, value: unknown) => {
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // приватный режим: просто не запоминаем
    }
};

const toPatch = (u: OrgUnitData): UnitPatch => ({
    id: u.id,
    name: u.name,
    description: u.description,
    order: u.order,
    parentId: u.parentId,
    unitTypeId: u.unitTypeId,
    managerId: u.managerId,
});

type Panel = { kind: "unit"; id: string } | { kind: "history" } | null;

export const OrgTreeView = () => (
    <ReactFlowProvider>
        <OrgTreeScreen />
    </ReactFlowProvider>
);

const OrgTreeScreen = observer(() => {
    const { orgTree: store, session, onec } = useMst();
    // справочник должностей виден тем, кому видны списки структуры
    const canSeePositions = canAny([spendingFunc.DEPARTMENT_READ, spendingFunc.DEPARTMENT_EDIT], session.access);
    const canImport = can(spendingFunc.ONEC_IMPORT, session.access);
    const flow = useReactFlow();

    useEffect(() => {
        store.load();
    }, [store]);

    const data = store.data;

    const [fullscreen, setFullscreen] = useState(false);
    const [showFired, setShowFired] = useState(false);
    const [organizationId, setOrganizationId] = useState("");
    const [expanded, setExpanded] = useState<Set<string>>(() => new Set(readJson<string[]>(EXPANDED_KEY, [])));
    const [query, setQuery] = useState("");
    const [hover, setHover] = useState<string | null>(null);
    const [panel, setPanel] = useState<Panel>(null);
    const [renaming, setRenaming] = useState<string | null>(null);
    const [adding, setAdding] = useState<{ parentId: string; anchor: HTMLElement } | null>(null);
    const [moving, setMoving] = useState<{ employee: OrgEmployeeData; to: OrgUnitData } | null>(null);
    const [card, setCard] = useState<{ id: string; presetDepartmentId?: string; presetPositionId?: string } | null>(
        null
    );
    const [typesOpen, setTypesOpen] = useState(false);
    const [positionsOpen, setPositionsOpen] = useState(false);
    // что импортируем из 1С: подразделения или штатное расписание
    const [importOpen, setImportOpen] = useState<"departments" | "staff" | null>(null);
    const [drag, setDrag] = useState<{ dragging: string | null; drop: DropTarget | null }>({
        dragging: null,
        drop: null,
    });
    const [zoom, setZoom] = useState(1);
    // «По размеру»: масштаб до вписывания — второй клик возвращает его
    const [beforeFit, setBeforeFit] = useState<Viewport | null>(null);
    const toggleFit = () => {
        if (beforeFit) {
            flow.setViewport(beforeFit, { duration: 500 });
            setBeforeFit(null);
        } else {
            setBeforeFit(flow.getViewport());
            flow.fitView({ padding: 0.12, maxZoom: 1, duration: 500 });
        }
    };
    const stageRef = useRef<HTMLDivElement>(null);
    const fitted = useRef(false);
    const pendingView = useRef(false);

    const canEdit = !!data?.canEdit;
    // переключателя режимов нет: с правом на структуру дерево сразу правится, без него - только чтение
    const editing = canEdit;
    const sizes = NORMAL_SIZES;

    useEffect(() => writeJson(EXPANDED_KEY, [...expanded]), [expanded]);

    const tree = useMemo(
        () => (data ? buildTree(data, { organizationId, showFired }) : null),
        [data, organizationId, showFired]
    );

    // первый показ и смена организации: если в дереве ничего не раскрыто, а наверху одно
    // подразделение — раскрываем цепочку, пока уровень единственный
    useEffect(() => {
        if (!tree || [...expanded].some((id) => tree.byId.has(id))) {
            return;
        }
        const next = new Set<string>();
        let level: TreeItem | undefined = tree.root;
        while (level) {
            const units: TreeItem[] = level.children.filter((c) => c.kind === "unit");
            if (units.length !== 1) {
                break;
            }
            next.add(units[0].id);
            level = units[0];
        }
        if (next.size) {
            setExpanded(next);
        }
        // только при первом построении дерева и при смене организации
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [!!tree, organizationId]);

    const hits = useMemo(
        () => (tree && data ? searchTree(tree, query, data.employees) : new Set<string>()),
        [tree, data, query]
    );

    // поиск раскрывает путь до найденного
    useEffect(() => {
        if (!tree || hits.size === 0) {
            return;
        }
        setExpanded((prev) => {
            const next = new Set(prev);
            hits.forEach((id) =>
                ancestors(tree, id)
                    .slice(1)
                    .forEach((a) => next.add(a))
            );
            return next;
        });
    }, [tree, hits]);

    const employeeHeight = useEmployeeHeight();
    const layout = useMemo(
        () => (tree ? layoutTree(tree, { sizes, expanded, edit: editing, employeeHeight }) : { nodes: [], edges: [] }),
        [tree, sizes, expanded, editing, employeeHeight]
    );

    // к первому найденному — плавно
    const firstHit = useMemo(() => [...hits][0] ?? null, [hits]);
    // раскладка на момент срабатывания таймера: найденное в свёрнутой ветке появляется в ней
    // только после раскрытия пути, а замыкание эффекта держит прежнюю
    const layoutRef = useRef(layout);
    layoutRef.current = layout;
    useEffect(() => {
        if (!firstHit) {
            return;
        }
        const t = setTimeout(() => {
            const n = layoutRef.current.nodes.find((x) => x.id === firstHit);
            if (n) {
                flow.setCenter(n.x + n.w / 2, n.y + n.h / 2, { zoom: Math.max(zoom, 0.8), duration: 600 });
            }
        }, 480);
        return () => clearTimeout(t);
        // по смене результата поиска, не по каждому кадру раскладки
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firstHit, query]);

    /**
     * Читаемый вид: если дерево помещается не мельче 85% — целиком, иначе в масштабе 85%
     * с организацией у левого края по центру. «По размеру» на длинной плоской структуре
     * ужимает карточки до нечитаемых — для него есть отдельная кнопка.
     */
    const readableView = useCallback(
        (nodes: { x: number; y: number; w: number; h: number; kind: string }[], duration = 500) => {
            const stage = stageRef.current;
            if (!stage || !nodes.length) {
                return;
            }
            const minX = Math.min(...nodes.map((n) => n.x));
            const minY = Math.min(...nodes.map((n) => n.y));
            const maxX = Math.max(...nodes.map((n) => n.x + n.w));
            const maxY = Math.max(...nodes.map((n) => n.y + n.h));
            const pad = 40;
            const fit = Math.min(
                (stage.clientWidth - pad * 2) / (maxX - minX),
                (stage.clientHeight - pad * 2) / (maxY - minY)
            );
            if (fit >= 0.85) {
                flow.fitView({ padding: 0.12, maxZoom: 1, duration });
                return;
            }
            const zoom = 0.85;
            const root = nodes.find((n) => n.kind === "org") ?? nodes[0];
            const y = stage.clientHeight / 2 - (root.y + root.h / 2) * zoom;
            // не оставлять пустоту над верхней карточкой
            const top = pad - minY * zoom;
            flow.setViewport({ x: pad - minX * zoom, y: Math.min(y, top), zoom }, { duration });
        },
        [flow]
    );

    // первый показ и «Свернуть/Развернуть всё»
    useEffect(() => {
        if (!layout.nodes.length) {
            return;
        }
        if (!fitted.current) {
            fitted.current = true;
            setTimeout(() => readableView(layout.nodes, 0), 60);
        } else if (pendingView.current) {
            // раскладка может перестроиться ещё раз (смена организации раскрывает цепочку) —
            // вид считаем по последней
            const t = setTimeout(() => {
                pendingView.current = false;
                readableView(layout.nodes);
            }, 120);
            return () => clearTimeout(t);
        }
    }, [layout.nodes, readableView]);

    const path = useMemo(() => (tree && hover ? new Set(ancestors(tree, hover)) : new Set<string>()), [tree, hover]);

    // новый узел: плавно к нему и короткая подсветка. Таймер не снимаем при смене focusId:
    // сброс focusId перезапускает эффект, и очистка отменила бы саму прокрутку
    const [focusId, setFocusId] = useState<string | null>(null);
    const [flash, setFlash] = useState<string | null>(null);
    useEffect(() => {
        const n = focusId ? layout.nodes.find((x) => x.id === focusId) : undefined;
        if (!n) {
            return;
        }
        setFocusId(null);
        // после анимации перестройки, иначе центр уедет за узлом
        setTimeout(() => {
            flow.setCenter(n.x + n.w / 2, n.y + n.h / 2, { zoom: Math.max(flow.getZoom(), 0.85), duration: 600 });
            setFlash(n.id);
        }, 450);
    }, [focusId, layout.nodes, flow]);
    useEffect(() => {
        if (!flash) {
            return;
        }
        const t = setTimeout(() => setFlash(null), 1800);
        return () => clearTimeout(t);
    }, [flash]);

    const dimmed = useMemo(() => {
        if (!tree || hits.size === 0) {
            return null;
        }
        const keep = new Set<string>();
        hits.forEach((id) => ancestors(tree, id).forEach((a) => keep.add(a)));
        const result = new Set<string>();
        layout.nodes.forEach((n) => {
            if (!keep.has(n.id) && n.kind !== "add") {
                result.add(n.id);
            }
        });
        return result;
    }, [tree, hits, layout.nodes]);

    const state = useMemo<TreeState | null>(() => {
        if (!data) {
            return null;
        }
        return {
            edit: editing,
            path,
            hovered: hover,
            flash,
            hits,
            dimmed,
            renaming,
            drop: drag.drop,
            dragging: drag.dragging,
            canOpenEmployee: data.canOpenEmployee,
            canEditEmployee: data.canEditEmployee,
            companyName: data.companyName,
            typesById: new Map(data.types.map((t) => [t.id, t])),
            employeesById: new Map(data.employees.map((e) => [e.id, e])),
            managerByUnit: new Map(data.units.filter((u) => u.managerId).map((u) => [u.id, u.managerId!])),
            apiBase: store.baseUrl,
        };
    }, [data, editing, path, hover, flash, hits, dimmed, renaming, drag, store]);

    /* ---------------- действия ---------------- */

    const latest = useRef({ store, tree, data, editing });
    latest.current = { store, tree, data, editing };

    /*
     * Карточка подразделения и форма нового подразделения открыты по одной: открывая одну,
     * закрываем другую. Если в открытой набраны несохранённые данные - сначала спрашиваем.
     * Признаки правок приходят от самих форм в ref: перерисовка дерева на каждый символ не нужна
     */
    const dirty = useRef({ panel: false, add: false });
    const [pendingClose, setPendingClose] = useState<(() => void) | null>(null);
    const guard = useCallback((action: () => void) => {
        if (dirty.current.panel || dirty.current.add) {
            setPendingClose(() => action);
        } else {
            action();
        }
    }, []);
    const discard = () => {
        const action = pendingClose;
        dirty.current = { panel: false, add: false };
        setPendingClose(null);
        action?.();
    };
    const setPanelDirty = useCallback((value: boolean) => {
        dirty.current.panel = value;
    }, []);
    const setAddDirty = useCallback((value: boolean) => {
        dirty.current.add = value;
    }, []);

    const currentPanel = useRef(panel);
    currentPanel.current = panel;
    const openPanel = useCallback(
        (next: Panel) => {
            const now = currentPanel.current;
            if (next?.kind === "unit" && now?.kind === "unit" && now.id === next.id) {
                return;
            }
            guard(() => {
                setAdding(null);
                setPanel(next);
            });
        },
        [guard]
    );
    const openAdding = useCallback(
        (parentId: string, anchor: HTMLElement) =>
            guard(() => {
                setPanel((p) => (p?.kind === "unit" ? null : p));
                setAdding({ parentId, anchor });
            }),
        [guard]
    );
    const closeAdding = useCallback(() => guard(() => setAdding(null)), [guard]);

    const expand = useCallback((ids: string[]) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => next.add(id));
            return next;
        });
    }, []);

    const addUnit = useCallback(
        async (parentId: string, values: { name: string; unitTypeId: string | null; description: string }) => {
            const created = await latest.current.store.createUnit({
                name: values.name,
                description: values.description,
                order: null,
                parentId: parentId === ROOT_ID ? null : parentId,
                unitTypeId: values.unitTypeId,
                managerId: null,
            });
            if (created) {
                expand([parentId]);
                // показать новый узел, когда раскладка его уложит
                setFocusId(created.id);
            }
            return !!created;
        },
        [expand]
    );

    const actions = useMemo<TreeActions>(
        () => ({
            toggle: (id) =>
                setExpanded((prev) => {
                    const next = new Set(prev);
                    if (next.has(id)) {
                        next.delete(id);
                    } else {
                        next.add(id);
                    }
                    return next;
                }),
            openUnit: (id) => openPanel({ kind: "unit", id }),
            openEmployee: (id) => setCard({ id }),
            hover: setHover,
            startRename: (id) => setRenaming(id),
            commitRename: (id, name) => {
                setRenaming(null);
                const unit = latest.current.store.unitById(id);
                if (unit && name) {
                    latest.current.store.updateUnit({ ...toPatch(unit), name }, `переименование «${unit.name}»`);
                }
            },
            addUnit: openAdding,
            addEmployee: (unitId, positionId) =>
                setCard({ id: "", presetDepartmentId: unitId, presetPositionId: positionId }),
            contextMenu: (id, event) => {
                event.preventDefault();
                const { tree: t, editing: e, data: d } = latest.current;
                const item = t?.byId.get(id);
                if (!item || !d) {
                    return;
                }
                const content =
                    item.kind === "unit" ? (
                        <Menu>
                            <MenuItem text="Открыть карточку" onClick={() => openPanel({ kind: "unit", id })} />
                            {e && (
                                <>
                                    <MenuItem text="Переименовать" onClick={() => setRenaming(id)} />
                                    <MenuDivider />
                                    <MenuItem
                                        text="Добавить подразделение"
                                        onClick={() => {
                                            expand([id]);
                                            setTimeout(() => {
                                                const btn = document.querySelector<HTMLElement>(
                                                    `[data-id="add:${id}"] button`
                                                );
                                                if (btn) {
                                                    openAdding(id, btn);
                                                }
                                            }, 480);
                                        }}
                                    />
                                    {d.canEditEmployee && (
                                        <MenuItem
                                            text="Добавить сотрудника"
                                            onClick={() => setCard({ id: "", presetDepartmentId: id })}
                                        />
                                    )}
                                    <MenuDivider />
                                    <MenuItem
                                        text="Удалить"
                                        intent="danger"
                                        disabled={item.unit?.system}
                                        onClick={() => openPanel({ kind: "unit", id })}
                                    />
                                </>
                            )}
                        </Menu>
                    ) : item.kind === "vacancy" && e && d.canEditEmployee && item.parentId ? (
                        <Menu>
                            <MenuItem
                                text="Принять сотрудника на вакансию"
                                onClick={() =>
                                    setCard({
                                        id: "",
                                        presetDepartmentId: item.parentId!,
                                        presetPositionId: item.vacancy?.positionId,
                                    })
                                }
                            />
                            <MenuItem
                                text="Штат подразделения"
                                onClick={() => openPanel({ kind: "unit", id: item.parentId! })}
                            />
                        </Menu>
                    ) : item.kind === "employee" && d.canOpenEmployee ? (
                        <Menu>
                            <MenuItem text="Открыть карточку" onClick={() => setCard({ id })} />
                        </Menu>
                    ) : null;
                if (content) {
                    showContextMenu({ content, targetOffset: { left: event.clientX, top: event.clientY } });
                }
            },
        }),
        [expand, openPanel, openAdding]
    );

    const onDrop = useCallback(
        ({ dragged, target, mode }: DropRequest) => {
            const { store: s, data: d } = latest.current;
            if (!d) {
                return;
            }

            if (dragged.kind === "employee" && target.unit && dragged.employee) {
                setMoving({ employee: dragged.employee, to: target.unit });
                return;
            }

            if (dragged.kind !== "unit") {
                return;
            }

            if (mode === "inside") {
                const parentId = target.kind === "org" ? null : target.id;
                const count = d.units.filter((u) => (u.parentId ?? null) === parentId && u.id !== dragged.id).length;
                s.moveUnit(dragged.id, parentId, count);
                expand([target.id]);
                return;
            }

            // до/после соседа
            const parentId = target.unit?.parentId ?? null;
            const siblings = sortUnits(d.units.filter((u) => (u.parentId ?? null) === parentId && u.id !== dragged.id));
            const index = siblings.findIndex((u) => u.id === target.id) + (mode === "after" ? 1 : 0);
            s.moveUnit(dragged.id, parentId, Math.max(0, index));
        },
        [expand]
    );

    const onDragState = useCallback(
        (dragging: string | null, drop: DropTarget | null) => setDrag({ dragging, drop }),
        []
    );

    const setAll = useCallback(
        (open: boolean) => {
            if (!tree) {
                return;
            }
            if (!open) {
                setExpanded(new Set());
            } else {
                const ids: string[] = [];
                tree.byId.forEach((i) => i.kind === "unit" && ids.push(i.id));
                setExpanded(new Set(ids));
            }
            // вид подстроим, когда раскладка пересчитается
            pendingView.current = true;
        },
        [tree]
    );

    /* ---------------- горячие клавиши ---------------- */

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const typing = /input|textarea|select/i.test((e.target as HTMLElement)?.tagName ?? "");
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !typing && latest.current.editing) {
                e.preventDefault();
                latest.current.store.undo();
            } else if (e.key === "/" && !typing) {
                e.preventDefault();
                document.querySelector<HTMLInputElement>(`.${styles.searchBox} input`)?.focus();
            } else if (e.key === "Escape" && !typing && !(e.target as HTMLElement)?.closest?.(".bp6-portal")) {
                // Esc в окне поверх дерева закрывает только это окно
                if (panel) {
                    openPanel(null);
                } else if (fullscreen) {
                    setFullscreen(false);
                }
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [panel, fullscreen, openPanel]);

    /* ---------------- разметка ---------------- */

    const typeUsage = useMemo(() => {
        const usage = new Map<string, number>();
        data?.units.forEach((u) => u.unitTypeId && usage.set(u.unitTypeId, (usage.get(u.unitTypeId) ?? 0) + 1));
        return usage;
    }, [data]);

    const addingParent = adding && tree ? tree.byId.get(adding.parentId) : undefined;
    const movingFrom = moving && data ? (data.units.find((u) => u.id === moving.employee.departmentId) ?? null) : null;

    return (
        <div className={classNames(styles.root, { [styles.fullscreen]: fullscreen })}>
            <div className={styles.toolbar}>
                <div className={styles.zoomGroup} role="group" aria-label="Масштаб">
                    <button
                        type="button"
                        className={styles.toolBtn}
                        title="Уменьшить"
                        onClick={() => flow.zoomTo(stepZoom(flow.getZoom(), -1), { duration: 250 })}
                    >
                        <MinusIcon size={12} />
                    </button>
                    <span className={styles.zoomVal}>{Math.round(zoom * 100)}%</span>
                    <button
                        type="button"
                        className={styles.toolBtn}
                        title="Увеличить"
                        onClick={() => flow.zoomTo(stepZoom(flow.getZoom(), 1), { duration: 250 })}
                    >
                        <PlusIcon size={12} />
                    </button>
                    <button
                        type="button"
                        className={styles.toolBtn}
                        title={beforeFit ? "Вернуть прежний масштаб" : "По размеру экрана"}
                        onClick={toggleFit}
                    >
                        <FitIcon size={13} />
                    </button>
                </div>

                <div className={styles.pair} role="group" aria-label="Свернуть или развернуть всё">
                    <button
                        type="button"
                        className={styles.toolBtn}
                        onClick={() => {
                            setBeforeFit(null);
                            setAll(false);
                        }}
                        title="Свернуть всё"
                    >
                        <CollapseIcon size={13} />
                    </button>
                    <button
                        type="button"
                        className={styles.toolBtn}
                        onClick={() => {
                            setBeforeFit(null);
                            setAll(true);
                        }}
                        title="Развернуть всё"
                    >
                        <ExpandAllIcon size={13} />
                    </button>
                </div>

                <button
                    type="button"
                    className={classNames(styles.toolBtn, styles.toggle, { [styles.active]: showFired })}
                    onClick={() => setShowFired(!showFired)}
                >
                    Уволенные
                </button>

                {data && data.organizations.length > 1 && (
                    <div className={styles.orgSelect} title="Организация (юрлицо)">
                        <SelectField
                            value={organizationId}
                            emptyLabel="Все организации"
                            options={data.organizations.map((o) => ({ id: o.id, label: o.name }))}
                            onChange={(id) => {
                                // другое дерево — заново показать его целиком, а не в прежнем месте
                                pendingView.current = true;
                                setBeforeFit(null);
                                setOrganizationId(id);
                            }}
                        />
                    </div>
                )}

                <span className={styles.spacer} />

                {editing && (
                    <>
                        <button
                            type="button"
                            className={styles.toolBtn}
                            disabled={!store.canUndo}
                            onClick={() => store.undo()}
                            title={store.lastUndoLabel ? `Отменить ${store.lastUndoLabel} (Ctrl+Z)` : "Отменить"}
                        >
                            <UndoIcon size={13} /> Отменить
                        </button>
                        <button type="button" className={styles.toolBtn} onClick={() => setTypesOpen(true)}>
                            <TagIcon size={13} /> Типы
                        </button>
                    </>
                )}
                {canSeePositions && (
                    <button
                        type="button"
                        className={classNames(styles.toolBtn, styles.lightBtn)}
                        onClick={() => setPositionsOpen(true)}
                    >
                        <BriefcaseIcon size={13} /> Должности
                    </button>
                )}
                {canImport && (
                    <button
                        type="button"
                        className={styles.toolBtn}
                        onClick={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            showContextMenu({
                                content: (
                                    <Menu>
                                        <MenuItem text="Подразделения" onClick={() => setImportOpen("departments")} />
                                        <MenuItem text="Штат" onClick={() => setImportOpen("staff")} />
                                    </Menu>
                                ),
                                targetOffset: { left: rect.left, top: rect.bottom + 4 },
                            });
                        }}
                    >
                        <ImportIcon size={13} /> Импорт из 1С
                    </button>
                )}
                <button
                    type="button"
                    className={styles.toolBtn}
                    onClick={() => openPanel(panel?.kind === "history" ? null : { kind: "history" })}
                >
                    <HistoryIcon size={13} /> История
                </button>
                <button
                    type="button"
                    className={classNames(styles.toolBtn, styles.toggle, { [styles.active]: fullscreen })}
                    onClick={() => setFullscreen(!fullscreen)}
                    title={fullscreen ? "Свернуть (Esc)" : "На весь экран"}
                >
                    <ExpandIcon size={13} />
                </button>

                <div className={styles.searchBox} title="Подразделение, ФИО, должность или руководитель. Клавиша /">
                    <Search
                        query={query}
                        onSearch={setQuery}
                        placeholder="Поиск"
                        style={{ marginLeft: 0, width: "100%" }}
                    />
                    {query && <span className={styles.found}>{hits.size ? `найдено ${hits.size}` : "не найдено"}</span>}
                </div>
            </div>

            <div className={styles.stage} ref={stageRef}>
                {/* статус — поверх холста, а не в строке кнопок: появляясь, он сдвигал кнопки шапки */}
                <AnimatePresence mode="popLayout">
                    {store.status !== "idle" && (
                        <motion.span
                            key={store.status}
                            className={classNames(styles.status, styles.statusOverlay, styles[store.status])}
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 6 }}
                            transition={{ duration: 0.2 }}
                        >
                            {store.status === "saving" && <span className={styles.spinner} />}
                            {store.status === "saving"
                                ? "Сохранение…"
                                : store.status === "saved"
                                  ? "Сохранено"
                                  : "Ошибка сохранения"}
                        </motion.span>
                    )}
                </AnimatePresence>

                {state && tree && (
                    <TreeActionsContext.Provider value={actions}>
                        <TreeStateContext.Provider value={state}>
                            <OrgCanvas
                                tree={tree}
                                nodes={layout.nodes}
                                edges={layout.edges}
                                gapX={sizes.gapX}
                                edit={editing}
                                onDrop={onDrop}
                                onDragState={onDragState}
                                onZoom={setZoom}
                                onUserMove={() => setBeforeFit(null)}
                                onPaneClick={() => panel?.kind === "unit" && openPanel(null)}
                            />
                        </TreeStateContext.Provider>
                    </TreeActionsContext.Provider>
                )}

                {!data && (
                    <div className={styles.empty}>
                        <div>
                            <b>{store.loading ? "Загружаем структуру…" : "Структура не загружена"}</b>
                        </div>
                    </div>
                )}

                {data && tree && tree.root.children.length === 0 && !editing && (
                    <div className={styles.empty}>
                        <div>
                            <b>Подразделений пока нет</b>
                            {canEdit ? "Добавьте первое подразделение" : ""}
                        </div>
                    </div>
                )}

                <AnimatePresence>
                    {panel?.kind === "unit" && tree && data && (
                        <UnitPanel
                            key={panel.id}
                            tree={tree}
                            unitId={panel.id}
                            types={data.types}
                            positions={data.positions ?? []}
                            employees={data.employees}
                            edit={editing}
                            onClose={() => openPanel(null)}
                            onDirtyChange={setPanelDirty}
                            onSave={(patch) => store.updateUnit(patch)}
                            onSaveStaff={(id, staff) => store.saveStaff(id, staff)}
                            canOpenPositions={canSeePositions}
                            onPositionsChanged={() => store.reload()}
                            onDelete={(id) => store.deleteUnit(id)}
                        />
                    )}
                    {panel?.kind === "history" && (
                        <HistoryPanel key="history" store={store as OrgTreeStoreType} onClose={() => openPanel(null)} />
                    )}
                </AnimatePresence>
            </div>

            {adding && addingParent && data && (
                <AnchoredPopover anchor={adding.anchor} onClose={closeAdding}>
                    <AddUnitForm
                        parentName={addingParent.unit?.name ?? data.companyName}
                        types={data.types}
                        defaultTypeId={defaultChildType(addingParent, data.types)}
                        onSubmit={(values) => addUnit(adding.parentId, values)}
                        onCancel={() => setAdding(null)}
                        onDirtyChange={setAddDirty}
                    />
                </AnchoredPopover>
            )}

            {pendingClose && <DiscardChangesDialog onDiscard={discard} onStay={() => setPendingClose(null)} />}

            {moving && data && (
                <MoveEmployeeDialog
                    employee={moving.employee}
                    from={movingFrom}
                    to={moving.to}
                    isManager={!!movingFrom && movingFrom.managerId === moving.employee.id}
                    canChangePosition={data.canEditEmployee}
                    loadPositions={store.loadPositions}
                    canSalary={can(spendingFunc.SCHEDULE_EDIT, session.access)}
                    loadConditions={store.loadConditions}
                    onMove={(date, positionId, pay) =>
                        store.moveEmployee(moving.employee.id, moving.to.id, date, positionId, pay)
                    }
                    onClose={() => setMoving(null)}
                />
            )}

            {typesOpen && data && (
                <TypesDialog
                    types={data.types}
                    usage={typeUsage}
                    onSave={(t) => store.saveType(t)}
                    onDelete={(id) => store.deleteType(id)}
                    onClose={() => setTypesOpen(false)}
                />
            )}

            {canImport && (
                <OneCImportDialog
                    isOpen={!!importOpen}
                    onClose={() => setImportOpen(null)}
                    store={onec}
                    entityKey={importOpen ?? "departments"}
                    title={importOpen === "staff" ? "Импорт штата из 1С" : "Импорт подразделений из 1С"}
                    roles={importOpen === "staff" ? STAFF_ROLES : DEPARTMENT_ROLES}
                    onImported={() => store.reload()}
                />
            )}

            {positionsOpen && (
                <PositionsDictionaryDialog
                    onClose={() => {
                        setPositionsOpen(false);
                        store.reload();
                    }}
                />
            )}

            {card && (
                <EmployeeCard
                    employerId={card.id}
                    presetDepartmentId={card.presetDepartmentId}
                    presetPositionId={card.presetPositionId}
                    onClose={() => setCard(null)}
                    onChanged={() => store.reload()}
                />
            )}
        </div>
    );
});

/** Тип нового узла по умолчанию: как у соседей, а если их нет — следующий по справочнику после родителя */
function defaultChildType(parent: TreeItem, types: { id: string; order: number | null }[]) {
    const siblings = parent.children.filter((c) => c.kind === "unit" && c.unit?.unitTypeId);
    if (siblings.length) {
        return siblings[siblings.length - 1].unit!.unitTypeId!;
    }
    if (parent.unit?.unitTypeId) {
        const index = types.findIndex((t) => t.id === parent.unit!.unitTypeId);
        return types[index + 1]?.id ?? parent.unit.unitTypeId;
    }
    return "";
}

/** шаг кнопок масштаба - 25%, с выравниванием на кратное: 85% -> 100% или 75% */
const ZOOM_STEP = 0.25;

const stepZoom = (current: number, direction: 1 | -1) => {
    const steps = current / ZOOM_STEP;
    const next = direction > 0 ? (Math.floor(steps + 1e-6) + 1) * ZOOM_STEP : (Math.ceil(steps - 1e-6) - 1) * ZOOM_STEP;

    // пределы - те же, что у холста (OrgCanvas: minZoom/maxZoom)
    return Math.min(1.6, Math.max(0.2, next));
};
